import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { getFullImageUrl } from '../utils/imageUtils';
import { API_URL } from '../config/api';
import './ItemDetailModal.css';

// Filter product variations by order type (delivery / pickup / eatIn). When deliveryMethod is null, show all that have at least one.
export function filterVariationsByOrderType(variations, deliveryMethod) {
  if (!variations || !Array.isArray(variations)) return [];
  if (!deliveryMethod) {
    return variations.filter((v) => {
      const showIn = v.showIn;
      if (!showIn || typeof showIn !== 'object') return true;
      return showIn.delivery !== false || showIn.pickup !== false || showIn.eatIn !== false;
    });
  }
  return variations.filter((v) => {
    const showIn = v.showIn;
    if (!showIn || typeof showIn !== 'object') return true;
    return showIn[deliveryMethod] !== false;
  });
}

const ItemDetailModal = ({ isOpen, onClose, item, restaurantId, restaurantName, isProduct = false, isEditing = false, existingVariations = null, existingIngredients = null, onSaveVariations = null, onSelectRelatedItem = null, orderingDisabled = false, soldOut = false, deliveryMethod = null }) => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const displayVariations = isProduct && item?.variations ? filterVariationsByOrderType(item.variations, deliveryMethod) : (item?.variations || []);
  const [quantity, setQuantity] = useState(isEditing ? (item?.quantity || 1) : 1);
  const [selectedVariations, setSelectedVariations] = useState(isEditing && existingVariations ? existingVariations : {});
  const [selectedIngredients, setSelectedIngredients] = useState(() => {
    if (isEditing && existingIngredients && Array.isArray(existingIngredients)) return [...existingIngredients];
    if (item?.ingredients && Array.isArray(item.ingredients)) return [...item.ingredients];
    return [];
  });
  const [orderedWithProducts, setOrderedWithProducts] = useState([]);

  useEffect(() => {
    if (isOpen && item) {
      if (isEditing && existingIngredients && Array.isArray(existingIngredients)) {
        setSelectedIngredients([...existingIngredients]);
      } else if (!isEditing && item.ingredients && Array.isArray(item.ingredients)) {
        setSelectedIngredients([...item.ingredients]);
      } else {
        setSelectedIngredients([]);
      }
    }
  }, [isOpen, item, isEditing, existingIngredients]);

  useEffect(() => {
    if (!isOpen || !isProduct || !restaurantId || !item?._id) {
      setOrderedWithProducts([]);
      return;
    }
    let cancelled = false;
    const fetchOrderedWith = async () => {
      try {
        const res = await axios.get(`${API_URL}/stores/${restaurantId}/products/ordered-with/${item._id}`);
        if (!cancelled && Array.isArray(res.data)) setOrderedWithProducts(res.data);
      } catch (err) {
        if (!cancelled) setOrderedWithProducts([]);
      }
    };
    fetchOrderedWith();
    return () => { cancelled = true; };
  }, [isOpen, isProduct, restaurantId, item && item._id]);

  if (!isOpen || !item) return null;

  const handleVariationChange = (variationName, optionName, isMultiple) => {
    setSelectedVariations(prev => {
      if (isMultiple) {
        const current = prev[variationName] || [];
        const updated = current.includes(optionName)
          ? current.filter(o => o !== optionName)
          : [...current, optionName];
        return { ...prev, [variationName]: updated };
      } else {
        return { ...prev, [variationName]: optionName };
      }
    });
  };

  const calculateTotalPrice = () => {
    let total = item.price || 0;
    const vars = displayVariations.length ? displayVariations : (item?.variations || []);
    if (vars.length) {
      vars.forEach(variation => {
        const selected = selectedVariations[variation.name];
        if (selected) {
          if (Array.isArray(selected)) {
            selected.forEach(optionName => {
              const option = variation.options?.find(o => o.name === optionName);
              if (option) total += option.price || 0;
            });
          } else {
            const option = variation.options?.find(o => o.name === selected);
            if (option) total += option.price || 0;
          }
        }
      });
    }
    return total * quantity;
  };

  const handleIngredientToggle = (ingredientName) => {
    setSelectedIngredients(prev =>
      prev.includes(ingredientName)
        ? prev.filter(i => i !== ingredientName)
        : [...prev, ingredientName]
    );
  };

  const handleAddToOrder = () => {
    if (isEditing && onSaveVariations) {
      // Editing mode - save variations and ingredients
      onSaveVariations(selectedVariations, selectedIngredients);
    } else {
      // Adding new item
      const itemWithVariations = {
        ...item,
        selectedVariations: selectedVariations,
        selectedIngredients: item?.ingredients?.length ? selectedIngredients : undefined,
        quantity: quantity
      };
      addToCart(restaurantId, restaurantName, itemWithVariations, isProduct ? 'store' : 'restaurant');
      onClose();
      setQuantity(1);
      setSelectedVariations({});
      setSelectedIngredients(item?.ingredients ? [...item.ingredients] : []);
    }
  };

  return (
    <div className="item-detail-modal-overlay" onClick={onClose}>
      <div className="item-detail-modal-content" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="item-detail-modal-close"
          onClick={onClose}
          aria-label={t('cart.close') || 'Close'}
        >
          <i className="fas fa-times"></i>
        </button>
        {item.image && (
          <div className="item-detail-banner">
            <img src={getFullImageUrl(item.image)} alt={item.name} />
          </div>
        )}
        <div className="item-detail-body">
          <h2 className="item-detail-title">{item.name}</h2>
          <p className="item-detail-price">€{item.price?.toFixed(2)}</p>
          
          {item.description && (
            <p className="item-detail-description">{item.description}</p>
          )}

          {item.ingredients && item.ingredients.length > 0 && (
            <div className="item-detail-ingredients">
              <label className="ingredients-label">{isEditing ? t('product.ingredients') : t('product.ingredientsExclude')}</label>
              <div className="ingredients-options">
                {item.ingredients.map((ing, idx) => (
                  <label key={idx} className="ingredient-option">
                    <input
                      type="checkbox"
                      checked={selectedIngredients.includes(ing)}
                      onChange={() => handleIngredientToggle(ing)}
                    />
                    <span>{ing}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {displayVariations.length > 0 && (
            <div className="item-detail-variations">
              {displayVariations.map((variation, vIndex) => (
                <div key={vIndex} className="variation-group">
                  <label className="variation-label">
                    {variation.name}
                    {variation.required && <span className="required">*</span>}
                  </label>
                  <div className="variation-options">
                    {variation.options?.map((option, oIndex) => {
                      const isSelected = variation.type === 'multiple'
                        ? (selectedVariations[variation.name] || []).includes(option.name)
                        : selectedVariations[variation.name] === option.name;
                      
                      return (
                        <label key={oIndex} className="variation-option">
                          <input
                            type={variation.type === 'multiple' ? 'checkbox' : 'radio'}
                            name={variation.name}
                            checked={isSelected}
                            onChange={() => handleVariationChange(variation.name, option.name, variation.type === 'multiple')}
                          />
                          <span>{option.name}</span>
                          {option.price > 0 && (
                            <span className="option-price">+€{option.price.toFixed(2)}</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isEditing && (
            <div className="item-detail-quantity">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="quantity-btn">-</button>
              <span className="quantity-value">{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)} className="quantity-btn">+</button>
            </div>
          )}

          {!isEditing && (
            <div className="item-detail-total">
              <span>{t('cart.total')}: €{calculateTotalPrice().toFixed(2)}</span>
            </div>
          )}

          {orderingDisabled ? (
            <p className="item-detail-ordering-disabled">
              {soldOut ? (t('restaurant.soldOut') || 'Sold out') : t('restaurant.orderingPausedDueToCongestion')}
            </p>
          ) : (
            <button onClick={handleAddToOrder} className="add-to-order-btn">
              {isEditing ? t('product.saveChanges') : t('product.addToOrder')}
            </button>
          )}

          {!isEditing && isProduct && orderedWithProducts.length > 0 && (
            <div className="item-detail-ordered-with">
              <h3 className="ordered-with-title">{t('product.oftenOrderedWith') || 'Often ordered with'}</h3>
              <div className="ordered-with-slider">
                {orderedWithProducts.map((product) => (
                  <button
                    key={product._id}
                    type="button"
                    className="ordered-with-card"
                    onClick={() => onSelectRelatedItem && onSelectRelatedItem(product)}
                  >
                    {product.image && (
                      <div className="ordered-with-card-image">
                        <img src={getFullImageUrl(product.image)} alt={product.name} />
                      </div>
                    )}
                    <div className="ordered-with-card-info">
                      <span className="ordered-with-card-name">{product.name}</span>
                      <span className="ordered-with-card-price">€{product.price?.toFixed(2)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemDetailModal;
